import { Controller } from "@hotwired/stimulus"
import { createIcons, icons } from 'lucide'

export default class extends Controller {
  static targets = [
    "step",
    "stepTab",
    "stepCheck",
    "progressSegment",
    "progressText",
    "heroInput",
    "knockInput",
    "objectiveInput",
    "obstacleInput",
    "sentenceInput",
    "navigation",
    "livePreview",
    "heroCounter",
    "knockCounter",
    "objectiveCounter",
    "obstacleCounter",
    "polishButton",
    "polishButtonText",
    "polishPreview",
    "confirmDisplay",
    "confirmEditWrapper",
    "confirmEditInput",
    "makeChangesButton",
    "completeButton",
    "completeButtonText"
  ]

  static values = {
    currentStep: { type: Number, default: 1 },
    totalSteps: { type: Number, default: 6 },
    outlineUrl: String,
    generateHeroUrl: String,
    generateKnockUrl: String,
    generateObjectiveUrl: String,
    generateObstacleUrl: String,
    polishUrl: String,
    kidFriendly: { type: Boolean, default: false }
  }

  connect() {
    this.sidebarCount = 6
    this.showStep(this.currentStepValue)
    this.updateProgress()
    this.updateWordCounts()
    this.updateLivePreview()
    setTimeout(() => createIcons({ icons }), 0)
  }

  saveAndContinue() {
    // Validate current step has a value (skip validation for step 4 obstacle - it's optional)
    const currentInput = this.getCurrentInput()
    if (currentInput && currentInput.value.trim() === '' && this.currentStepValue !== 4) {
      currentInput.focus()
      currentInput.classList.add('border-danger-500', 'ring-2', 'ring-danger-200')
      setTimeout(() => {
        currentInput.classList.remove('border-danger-500', 'ring-2', 'ring-danger-200')
      }, 2000)
      return
    }

    // Save and move to next step
    this.save().then(() => {
      if (this.currentStepValue < this.totalStepsValue) {
        this.currentStepValue++
        this.showStep(this.currentStepValue)
        this.updateProgress()
      }
      // Note: Step 5 uses polishAndContinue, Step 6 uses completeConcept
    })
  }

  async polishAndContinue(event) {
    event?.preventDefault()

    if (!this.hasSentenceInputTarget || !this.hasPolishUrlValue) {
      // No polish URL, just continue
      this.goToConfirmStep()
      return
    }

    const currentLogline = this.sentenceInputTarget.value.trim()
    if (!currentLogline) {
      // Generate logline first if empty
      this.generateLogline()
      this.goToConfirmStep()
      return
    }

    // Show loading state
    if (this.hasPolishButtonTarget) {
      this.polishButtonTarget.disabled = true
    }
    if (this.hasPolishButtonTextTarget) {
      this.polishButtonTextTarget.textContent = "Polishing..."
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.polishUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify({
          logline: currentLogline,
          protagonist: this.hasHeroInputTarget ? this.heroInputTarget.value.trim() : "",
          incident: this.hasKnockInputTarget ? this.knockInputTarget.value.trim() : "",
          objective: this.hasObjectiveInputTarget ? this.objectiveInputTarget.value.trim() : "",
          antagonist: this.hasObstacleInputTarget ? this.obstacleInputTarget.value.trim() : ""
        })
      })

      const data = await response.json()

      if (data.success && data.polished) {
        this.sentenceInputTarget.value = data.polished
      }
      // Whether polish succeeded or not, continue to confirm step
      this.save().then(() => {
        this.goToConfirmStep()
      })
    } catch (error) {
      console.error("Polish error:", error)
      // Continue anyway
      this.goToConfirmStep()
    } finally {
      if (this.hasPolishButtonTarget) {
        this.polishButtonTarget.disabled = false
      }
      if (this.hasPolishButtonTextTarget) {
        this.polishButtonTextTarget.textContent = "Polish"
      }
    }
  }

  goToConfirmStep() {
    this.currentStepValue = this.totalStepsValue
    this.showStep(this.currentStepValue)
    this.updateProgress()
  }

  getCurrentInput() {
    switch (this.currentStepValue) {
      case 1: return this.hasHeroInputTarget ? this.heroInputTarget : null
      case 2: return this.hasKnockInputTarget ? this.knockInputTarget : null
      case 3: return this.hasObjectiveInputTarget ? this.objectiveInputTarget : null
      case 4: return this.hasObstacleInputTarget ? this.obstacleInputTarget : null
      case 5: return this.hasSentenceInputTarget ? this.sentenceInputTarget : null
      case 6: return this.hasConfirmEditInputTarget ? this.confirmEditInputTarget : null
      default: return null
    }
  }

  goToStep(event) {
    const step = parseInt(event.currentTarget.dataset.step)
    // Only allow going to steps that are completed or the next available step
    const completedSteps = this.getCompletedSteps()
    if (step <= completedSteps + 1 && step >= 1 && step <= this.totalStepsValue) {
      this.currentStepValue = step
      this.showStep(step)
    }
  }

  getCompletedSteps() {
    let completed = 0
    if (this.hasHeroInputTarget && this.heroInputTarget.value.trim()) completed++
    if (this.hasKnockInputTarget && this.knockInputTarget.value.trim()) completed++
    if (this.hasObjectiveInputTarget && this.objectiveInputTarget.value.trim()) completed++
    if (this.hasObstacleInputTarget && this.obstacleInputTarget.value.trim()) completed++
    if (this.hasSentenceInputTarget && this.sentenceInputTarget.value.trim()) completed++
    return completed
  }

  showStep(stepNumber) {
    // Steps are structured as: first 6 are sidebars, next 6 are main content
    this.stepTargets.forEach((step, index) => {
      const sidebarIndex = stepNumber - 1
      const mainIndex = stepNumber - 1 + this.sidebarCount
      const shouldShow = (index === sidebarIndex) || (index === mainIndex)
      step.classList.toggle("hidden", !shouldShow)
    })

    // Update tab styling based on completion, not current position
    this.updateTabStylingWithCheckmarks(stepNumber)

    // Handle polish step (step 5)
    const isPolishStep = stepNumber === 5
    // Handle confirm step (step 6)
    const isConfirmStep = stepNumber === this.totalStepsValue

    if (isPolishStep) {
      // Auto-generate logline if empty
      if (this.hasSentenceInputTarget && !this.sentenceInputTarget.value.trim()) {
        this.generateLogline()
      }
      // Populate polish preview with current sentence
      if (this.hasPolishPreviewTarget && this.hasSentenceInputTarget) {
        this.polishPreviewTarget.textContent = this.sentenceInputTarget.value || "No concept entered yet."
      }
      // Hide navigation on polish step (only Polish button)
      if (this.hasNavigationTarget) {
        this.navigationTarget.classList.add("hidden")
      }
    } else if (isConfirmStep) {
      // Populate confirm text with current sentence
      if (this.hasConfirmDisplayTarget && this.hasSentenceInputTarget) {
        this.confirmDisplayTarget.textContent = this.sentenceInputTarget.value || "No concept entered yet."
      }
      // Reset to read-only mode
      this.resetConfirmDisplay()
      // Hide navigation on confirm step
      if (this.hasNavigationTarget) {
        this.navigationTarget.classList.add("hidden")
      }
    } else {
      // Show navigation on steps 1-4
      if (this.hasNavigationTarget) {
        this.navigationTarget.classList.remove("hidden")
      }
    }

    // Re-init icons
    setTimeout(() => createIcons({ icons }), 0)
  }

  resetConfirmDisplay() {
    // Show read-only display, hide editable
    if (this.hasConfirmDisplayTarget) {
      this.confirmDisplayTarget.classList.remove("hidden")
    }
    if (this.hasConfirmEditWrapperTarget) {
      this.confirmEditWrapperTarget.classList.add("hidden")
    }
    // Show "make changes" button
    if (this.hasMakeChangesButtonTarget) {
      this.makeChangesButtonTarget.classList.remove("hidden")
    }
  }

  enableConfirmEdit() {
    // Hide read-only display, show editable
    if (this.hasConfirmDisplayTarget) {
      this.confirmDisplayTarget.classList.add("hidden")
    }
    if (this.hasConfirmEditWrapperTarget) {
      this.confirmEditWrapperTarget.classList.remove("hidden")
      // Populate with current value
      if (this.hasConfirmEditInputTarget && this.hasSentenceInputTarget) {
        this.confirmEditInputTarget.value = this.sentenceInputTarget.value
        this.confirmEditInputTarget.focus()
      }
    }
    // Hide "make changes" button since we're now in edit mode
    if (this.hasMakeChangesButtonTarget) {
      this.makeChangesButtonTarget.classList.add("hidden")
    }
  }

  confirmInputChanged() {
    // Sync edits back to the main sentence input
    if (this.hasConfirmEditInputTarget && this.hasSentenceInputTarget) {
      this.sentenceInputTarget.value = this.confirmEditInputTarget.value
    }
    // Also update the display text for when they finish editing
    if (this.hasConfirmDisplayTarget && this.hasConfirmEditInputTarget) {
      this.confirmDisplayTarget.textContent = this.confirmEditInputTarget.value
    }
    this.updateProgress()
  }

  completeConcept() {
    // Save and redirect to outline
    this.save().then(() => {
      if (this.hasOutlineUrlValue) {
        window.location.href = this.outlineUrlValue
      }
    })
  }

  updateProgress() {
    const completedSteps = this.getCompletedSteps()
    const progress = (completedSteps / this.totalStepsValue) * 100

    // Update segmented progress bar with gradient
    this.progressSegmentTargets.forEach((segment, index) => {
      const stepInput = this.getInputForStep(index + 1)
      const hasValue = stepInput && stepInput.value.trim() !== ''

      // Remove old classes
      segment.classList.remove("bg-primary-500", "bg-success-500")
      segment.style.background = ""

      if (hasValue) {
        segment.style.width = "100%"
        // Pink for progress
        segment.style.background = `#ec4899`
      } else {
        segment.style.width = "0%"
        segment.style.background = ""
      }
    })

    this.progressTextTarget.textContent = `${Math.round(progress)}% complete`
  }

  getInputForStep(step) {
    switch (step) {
      case 1: return this.hasHeroInputTarget ? this.heroInputTarget : null
      case 2: return this.hasKnockInputTarget ? this.knockInputTarget : null
      case 3: return this.hasObjectiveInputTarget ? this.objectiveInputTarget : null
      case 4: return this.hasObstacleInputTarget ? this.obstacleInputTarget : null
      case 5: return this.hasSentenceInputTarget ? this.sentenceInputTarget : null
      case 6: return this.hasSentenceInputTarget ? this.sentenceInputTarget : null // Use sentence for step 6 completion check
      default: return null
    }
  }

  // Update UI on input change (save happens on continue)
  inputChanged() {
    this.updateProgress()
    this.updateTabStyling()
    this.updateLivePreview()
  }

  updateTabStyling() {
    this.updateTabStylingWithCheckmarks(this.currentStepValue)
  }

  updateTabStylingWithCheckmarks(currentStep) {
    // Color scales from light to dark (left to right) - 6 steps
    const limeShades = ['bg-lime-300', 'bg-lime-400', 'bg-lime-500', 'bg-lime-600', 'bg-lime-700', 'bg-lime-800']
    const cyanShades = ['bg-cyan-300', 'bg-cyan-400', 'bg-cyan-500', 'bg-cyan-600', 'bg-cyan-700', 'bg-cyan-800']
    const allShades = [...limeShades, ...cyanShades]

    this.stepTabTargets.forEach((tab, index) => {
      const tabStep = index + 1
      const stepInput = this.getInputForStep(tabStep)
      const isCompleted = stepInput && stepInput.value.trim() !== ''
      const isActive = tabStep === currentStep
      const checkIcon = this.stepCheckTargets[index]

      // Remove all state classes
      tab.classList.remove(...allShades, "bg-transparent", "text-white", "text-gray-500", "border-2", "border-gray-300", "ring-2", "ring-offset-2", "ring-lime-500")

      if (isCompleted) {
        // Completed - lime green gradient (light to dark) with checkmark
        tab.classList.add(limeShades[index], "text-white")
        if (isActive) {
          // Add green outline if also active
          tab.classList.add("ring-2", "ring-offset-2", "ring-lime-500")
        }
        if (checkIcon) {
          checkIcon.classList.remove("hidden")
        }
      } else if (isActive) {
        // Current (not completed yet) - cyan gradient (light to dark)
        tab.classList.add(cyanShades[index], "text-white")
        if (checkIcon) {
          checkIcon.classList.add("hidden")
        }
      } else {
        // Not started - empty with gray border
        tab.classList.add("bg-transparent", "border-2", "border-gray-300", "text-gray-500")
        if (checkIcon) {
          checkIcon.classList.add("hidden")
        }
      }
    })

    // Re-init icons for checkmarks
    setTimeout(() => createIcons({ icons }), 0)
  }

  save() {
    const form = this.element.querySelector('form')
    if (!form) return Promise.resolve()

    const formData = new FormData(form)

    return fetch(form.action, {
      method: 'PATCH',
      body: formData,
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    }).then(response => {
      if (response.ok) {
        this.showSavedIndicator()
      }
      return response
    }).catch(error => {
      console.error('Save failed:', error)
    })
  }

  showSavedIndicator() {
    const text = this.progressTextTarget
    const originalText = text.textContent
    text.textContent = "Saved!"
    text.classList.add("text-success-600")
    setTimeout(() => {
      text.textContent = originalText
      text.classList.remove("text-success-600")
    }, 1000)
  }

  updateWordCount(event) {
    const input = event.target
    const stepEl = input.closest('[data-logline-wizard-target="step"]')
    if (!stepEl) return

    const countTarget = stepEl.querySelector('[data-word-count]')
    if (countTarget) {
      const maxWords = parseInt(input.dataset.maxWords) || 5
      const words = input.value.trim().split(/\s+/).filter(w => w.length > 0).length
      const remaining = maxWords - words
      countTarget.textContent = `You have ${remaining} word${remaining === 1 ? '' : 's'} left`
      countTarget.classList.toggle("text-danger-600", remaining < 0)
      countTarget.classList.toggle("text-gray-500", remaining >= 0)
    }
  }

  updateWordCounts() {
    if (this.hasHeroInputTarget) {
      this.updateWordCount({ target: this.heroInputTarget })
    }
    if (this.hasKnockInputTarget) {
      this.updateWordCount({ target: this.knockInputTarget })
    }
    if (this.hasObjectiveInputTarget) {
      this.updateWordCount({ target: this.objectiveInputTarget })
    }
    if (this.hasObstacleInputTarget) {
      this.updateWordCount({ target: this.obstacleInputTarget })
    }
  }

  generateLogline() {
    const hero = this.hasHeroInputTarget ? this.heroInputTarget.value.trim() : ''
    const knock = this.hasKnockInputTarget ? this.knockInputTarget.value.trim() : ''
    const objective = this.hasObjectiveInputTarget ? this.objectiveInputTarget.value.trim() : ''
    const obstacle = this.hasObstacleInputTarget ? this.obstacleInputTarget.value.trim() : ''

    if (hero && knock && objective) {
      // Normalize: lowercase first letter, remove trailing punctuation
      const normalize = (str) => {
        let s = str.replace(/[.!?]+$/, '') // remove trailing punctuation
        return s.charAt(0).toLowerCase() + s.slice(1)
      }

      let sentence = `When ${hero} ${normalize(knock)}, they must ${normalize(objective)}`
      if (obstacle) {
        sentence += ` while facing ${normalize(obstacle)}`
      }
      sentence += "."
      if (this.hasSentenceInputTarget) {
        this.sentenceInputTarget.value = sentence
      }
    }
  }

  generateHero(event) {
    event.preventDefault()
    const counter = this.hasHeroCounterTarget ? this.heroCounterTarget : null
    this.callGenerateApi(this.generateHeroUrlValue, this.heroInputTarget, counter)
  }

  generateKnock(event) {
    event.preventDefault()
    const counter = this.hasKnockCounterTarget ? this.knockCounterTarget : null
    this.callGenerateApi(this.generateKnockUrlValue, this.knockInputTarget, counter)
  }

  generateObjective(event) {
    event.preventDefault()
    const counter = this.hasObjectiveCounterTarget ? this.objectiveCounterTarget : null
    this.callGenerateApi(this.generateObjectiveUrlValue, this.objectiveInputTarget, counter)
  }

  generateObstacle(event) {
    event.preventDefault()
    const counter = this.hasObstacleCounterTarget ? this.obstacleCounterTarget : null
    this.callGenerateApi(this.generateObstacleUrlValue, this.obstacleInputTarget, counter)
  }

  callGenerateApi(url, targetInput, counterEl) {
    if (!url || !targetInput) return

    // Show loading state
    const originalValue = targetInput.value
    targetInput.value = "Generating..."
    targetInput.disabled = true

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ kid_friendly: this.kidFriendlyValue }),
      credentials: 'same-origin'
    })
    .then(response => response.json())
    .then(data => {
      targetInput.disabled = false
      if (data.success && data.result) {
        targetInput.value = data.result
        this.updateWordCount({ target: targetInput })
        this.updateLivePreview()
        if (counterEl && data.remaining !== undefined) {
          this.updateCounter(counterEl, data.remaining)
        }
      } else if (data.limit_reached) {
        targetInput.value = originalValue
        if (counterEl) {
          this.updateCounter(counterEl, 0)
        }
      } else {
        targetInput.value = originalValue
        console.error('Generate failed:', data.error)
        this.showGenerationError(data.error)
      }
    })
    .catch(error => {
      targetInput.disabled = false
      targetInput.value = originalValue
      console.error('Generate request failed:', error)
      this.showGenerationError('We couldn\'t generate an idea right now. Please try again.')
    })
  }

  showGenerationError(message) {
    // Keep the failure visible to the writer; previously this only appeared in
    // the browser console, which made the button look like it did nothing.
    const error = document.createElement('p')
    error.className = 'mt-2 text-sm text-red-600 text-center'
    error.textContent = message || 'We couldn\'t generate an idea right now. Please try again.'
    this.element.querySelector('.text-red-600[data-generation-error]')?.remove()
    error.dataset.generationError = 'true'
    const currentStep = this.stepTargets.find(step => !step.classList.contains('hidden'))
    currentStep?.appendChild(error)
    setTimeout(() => error.remove(), 5000)
  }

  updateCounter(counterEl, remaining) {
    if (remaining <= 0) {
      counterEl.textContent = "(limit reached)"
      counterEl.classList.remove("text-gray-400")
      counterEl.classList.add("text-amber-500")
    } else {
      counterEl.textContent = `(${remaining} left)`
      counterEl.classList.remove("text-amber-500")
      counterEl.classList.add("text-gray-400")
    }
  }

  updateLivePreview() {
    if (!this.hasLivePreviewTarget) return

    const hero = this.hasHeroInputTarget ? this.heroInputTarget.value.trim() : ''
    const knock = this.hasKnockInputTarget ? this.knockInputTarget.value.trim() : ''
    const objective = this.hasObjectiveInputTarget ? this.objectiveInputTarget.value.trim() : ''
    const obstacle = this.hasObstacleInputTarget ? this.obstacleInputTarget.value.trim() : ''

    // Build preview with placeholders for missing parts
    const normalize = (str) => {
      if (!str) return ''
      let s = str.replace(/[.!?]+$/, '') // remove trailing punctuation
      return s.charAt(0).toLowerCase() + s.slice(1)
    }

    let parts = []

    // "When [hero]..."
    if (hero) {
      parts.push(`When <strong>${hero}</strong>`)
    } else {
      parts.push(`When <span class="text-gray-400">[hero]</span>`)
    }

    // "...[knock]..."
    if (knock) {
      parts.push(`<strong>${normalize(knock)}</strong>`)
    } else {
      parts.push(`<span class="text-gray-400">[the knock]</span>`)
    }

    // ", they must [objective]..."
    if (objective) {
      parts.push(`, they must <strong>${normalize(objective)}</strong>`)
    } else {
      parts.push(`, they must <span class="text-gray-400">[goal]</span>`)
    }

    // "...while facing [obstacle]"
    if (obstacle) {
      parts.push(` while facing <strong>${normalize(obstacle)}</strong>`)
    } else if (hero || knock || objective) {
      // Only show placeholder if user has started
      parts.push(` while facing <span class="text-gray-400">[obstacle]</span>`)
    }

    // Add period if we have any content
    if (hero || knock || objective || obstacle) {
      this.livePreviewTarget.innerHTML = parts.join(' ') + '.'
    } else {
      this.livePreviewTarget.innerHTML = '<span class="text-gray-400">Your logline will appear here as you build it...</span>'
    }
  }

  async polishLogline(event) {
    event?.preventDefault()

    if (!this.hasSentenceInputTarget || !this.hasPolishUrlValue) return

    const currentLogline = this.sentenceInputTarget.value.trim()
    if (!currentLogline) {
      // Generate logline first if empty
      this.generateLogline()
      return
    }

    // Show loading state
    if (this.hasPolishButtonTarget) {
      this.polishButtonTarget.disabled = true
    }
    if (this.hasPolishButtonTextTarget) {
      this.polishButtonTextTarget.textContent = "Polishing..."
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.polishUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify({
          logline: currentLogline,
          protagonist: this.hasHeroInputTarget ? this.heroInputTarget.value.trim() : "",
          incident: this.hasKnockInputTarget ? this.knockInputTarget.value.trim() : "",
          objective: this.hasObjectiveInputTarget ? this.objectiveInputTarget.value.trim() : "",
          antagonist: this.hasObstacleInputTarget ? this.obstacleInputTarget.value.trim() : ""
        })
      })

      const data = await response.json()

      if (data.success && data.polished) {
        this.sentenceInputTarget.value = data.polished
        this.inputChanged()

        // Show success state briefly
        if (this.hasPolishButtonTextTarget) {
          this.polishButtonTextTarget.textContent = "Polished!"
          setTimeout(() => {
            this.polishButtonTextTarget.textContent = "Polish"
          }, 2000)
        }
      } else {
        if (this.hasPolishButtonTextTarget) {
          this.polishButtonTextTarget.textContent = "Try again"
          setTimeout(() => {
            this.polishButtonTextTarget.textContent = "Polish"
          }, 2000)
        }
      }
    } catch (error) {
      console.error("Polish error:", error)
      if (this.hasPolishButtonTextTarget) {
        this.polishButtonTextTarget.textContent = "Try again"
        setTimeout(() => {
          this.polishButtonTextTarget.textContent = "Polish"
        }, 2000)
      }
    } finally {
      if (this.hasPolishButtonTarget) {
        this.polishButtonTarget.disabled = false
      }
    }
  }
}
